import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl,
  SelectChangeEvent 
} from '@mui/material';
import { useSelector } from 'react-redux';
import SelectedCaptionDisplay from '../atom/SelectedCaptionDisplay';
import TranslateButton from '../atom/TranslateButton';
import { CHOOSE_TARGET_LANGUAGE_HEADER } from '../constants/TranslationConstants';

interface LanguageSelectionProps {
  selectedCaption: {
    fileName: string;
    language: string;
  };
  onTranslate: (targetLanguage: string) => void;
}

const LanguageSelection = ({ selectedCaption, onTranslate }: LanguageSelectionProps) => {
  const [targetLanguage, setTargetLanguage] = useState('');
  const isTranslating = useSelector((state: any) => state.translation.isTranslating);

  const languages = [
    'English',
    'Spanish',
    'French', 
    'Chinese',
    'German',
    'Italian',
    'Portuguese',
    'Russian',
    'Japanese',
    'Korean'
  ];

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    setTargetLanguage(event.target.value);
  };

  const handleTranslate = () => {
    onTranslate(targetLanguage);
  };

  return (
    <Box>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          fontWeight: 'medium',
          mb: 2,
          color: 'text.primary',
          fontSize: { xs: '1rem', md: '1.1rem' }
        }}
      >
        {CHOOSE_TARGET_LANGUAGE_HEADER}
      </Typography>

      <SelectedCaptionDisplay 
        fileName={selectedCaption.fileName}
        language={selectedCaption.language}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <Select
          value={targetLanguage}
          onChange={handleLanguageChange}
          displayEmpty
          sx={{
            height: '40px',
            backgroundColor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#006684',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#006684',
            }
          }}
        >
          <MenuItem value="" disabled>
            Select Language
          </MenuItem>
          {languages.map((language) => (
            <MenuItem 
              key={language} 
              value={language}
              disabled={language === selectedCaption.language}
            >
              {language === selectedCaption.language ? `${language} (Source)` : language}
            </MenuItem>
          ))}
          <MenuItem value="Etc">
            Etc...
          </MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TranslateButton
          onTranslate={handleTranslate}
          disabled={!targetLanguage || targetLanguage === '' || targetLanguage === selectedCaption.language || isTranslating}
          isLoading={isTranslating}
        />
      </Box>
    </Box>
  );
};

export default LanguageSelection;